import { useGetIntegrationRequestFormOptionsQuery } from '../../../../services';
import { ExistingIntegrationAlert } from '../ExistingIntegrationAlert';
import ApiServicesSection from './ApiServicesSection';
import RequestTypeSection from './RequestTypeSection';
import BranchesSection from './BranchesSection';
import ContactSection from './ContactSection';
import WebhookSection from './WebhookSection';
import FormLoadState from './FormLoadState';
import NotesSection from './NotesSection';
import FormActions from './FormActions';
import type {
  IntegrationRequest,
  IntegrationRequestPayload,
} from '../../../../models/IntegrationRequest';
import {
  useIntegrationRequestForm,
  useExistingIntegrationNotices,
  useIntegrationRequestFormChoices,
  useOperatorChangeReset,
  useSingleOperatorPreselection,
} from '../../hooks';

interface IntegrationRequestFormProps {
  commerceId: string;
  initialRequest?: IntegrationRequest;
  isSaving: boolean;
  submitLabel: string;
  onSubmit: (payload: IntegrationRequestPayload) => void;
  onCancel: () => void;
}

const IntegrationRequestForm = ({
  commerceId,
  initialRequest,
  isSaving,
  submitLabel,
  onSubmit,
  onCancel,
}: IntegrationRequestFormProps) => {
  const { data: options, isLoading } = useGetIntegrationRequestFormOptionsQuery(commerceId);
  const { form, submit, values } = useIntegrationRequestForm({
    commerceId,
    initialRequest,
    onSubmit,
  });
  const { control, setValue, reset } = form;

  useSingleOperatorPreselection({ options, enabled: !initialRequest, setValue });
  useOperatorChangeReset({ logisticOperatorId: values.logisticOperatorId, setValue });

  const choices = useIntegrationRequestFormChoices({
    options,
    logisticOperatorId: values.logisticOperatorId,
  });

  const notices = useExistingIntegrationNotices(options, {
    type: values.type,
    logisticOperatorId: values.logisticOperatorId,
    serviceWithOperatorIds: values.isApi ? values.serviceWithOperatorIds : [],
    webhookUrl: values.webhookUrl,
    webhookStatusIds: values.showsWebhook ? values.webhookStatusIds : [],
    currentRequestId: initialRequest?.id,
  });

  if (!options) {
    return <FormLoadState isLoading={isLoading} />;
  }

  const handleCancel = () => {
    reset();
    onCancel();
  };

  return (
    <form className='flex flex-col gap-6 pb-24' onSubmit={submit}>
      <RequestTypeSection
        control={control}
        commerce={options.commerce}
        operatorOptions={choices.operatorOptions}
        disabled={isSaving}
      />

      <ExistingIntegrationAlert notices={notices} />

      {values.isApi && (
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <ApiServicesSection
            control={control}
            serviceOptions={choices.serviceOptions}
            hasOperator={!!values.logisticOperatorId}
            disabled={isSaving}
          />
          <BranchesSection
            control={control}
            branchOptions={choices.branchOptions}
            allBranches={values.allBranches}
            disabled={isSaving}
          />
        </div>
      )}

      <ContactSection control={control} isApi={values.isApi} disabled={isSaving} />

      <WebhookSection
        control={control}
        statuses={choices.notifiableStatuses}
        isApi={values.isApi}
        showsWebhook={values.showsWebhook}
        disabled={isSaving}
      />

      <NotesSection control={control} disabled={isSaving} />

      <FormActions
        submitLabel={submitLabel}
        isSaving={isSaving}
        onCancel={handleCancel}
        onSubmit={submit}
      />
    </form>
  );
};

export default IntegrationRequestForm;
